import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend } from 'recharts';

interface DepartmentPerformanceComparisonChartProps {
  data: Array<{
    department: string;
    workshop_count: number;
    completion_rate: number;
    avg_progress: number;
    total_savings: number;
  }>;
  loading?: boolean;
}

const DepartmentPerformanceComparisonChart: React.FC<DepartmentPerformanceComparisonChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance Comparison</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance Comparison</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No department data available
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) {
      return `€${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `€${(value / 1000).toFixed(1)}K`;
    }
    return `€${value.toFixed(0)}`;
  };

  const getPerformanceLevel = (completionRate: number, avgProgress: number) => {
    const score = (completionRate + avgProgress) / 2;
    if (score >= 80) return { level: 'Excellent', color: '#10b981' };
    if (score >= 60) return { level: 'Good', color: '#3b82f6' };
    if (score >= 40) return { level: 'Average', color: '#f59e0b' };
    return { level: 'Needs Improvement', color: '#ef4444' };
  };

  const maxWorkshops = Math.max(...data.map(d => d.workshop_count));
  const maxSavings = Math.max(...data.map(d => d.total_savings));

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Department Performance Comparison</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="department" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              yAxisId="left"
              tick={{ fontSize: 12 }}
              domain={[0, 'dataMax + 2']}
            />
            <YAxis 
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrency}
            />
            <Tooltip 
              formatter={(value: number, name: string) => {
                if (name === 'workshop_count') return [`${value} workshops`, 'Workshop Count'];
                if (name === 'completion_rate') return [`${value.toFixed(1)}%`, 'Completion Rate'];
                if (name === 'avg_progress') return [`${value.toFixed(1)}%`, 'Average Progress'];
                if (name === 'total_savings') return [formatCurrency(value), 'Total Savings'];
                return [value, name];
              }}
              labelFormatter={(label) => `Department: ${label}`}
            />
            <Legend />
            <Bar 
              yAxisId="left"
              dataKey="workshop_count" 
              name="Workshop Count"
              radius={[4, 4, 0, 0]}
            >
              {data.map((entry, index) => {
                const performance = getPerformanceLevel(entry.completion_rate, entry.avg_progress);
                return (
                  <Cell 
                    key={`cell-count-${index}`} 
                    fill={performance.color} 
                  />
                );
              })}
            </Bar>
            <Bar 
              yAxisId="left"
              dataKey="completion_rate" 
              name="Completion Rate (%)"
              radius={[4, 4, 0, 0]}
              fill="#8b5cf6"
              opacity={0.7}
            />
            <Bar 
              yAxisId="right"
              dataKey="total_savings" 
              name="Total Savings"
              radius={[4, 4, 0, 0]}
              fill="#10b981"
              opacity={0.6}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Department Rankings</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => (b.completion_rate + b.avg_progress) - (a.completion_rate + a.avg_progress))
              .map((item, index) => {
                const performance = getPerformanceLevel(item.completion_rate, item.avg_progress);
                return (
                  <div key={item.department} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <span className="text-sm font-medium mr-2">#{index + 1}</span>
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: performance.color }}
                      ></div>
                      <span className="text-sm font-medium">{item.department}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.workshop_count}</span> workshops
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: performance.color + '20', 
                        color: performance.color 
                      }}>
                        {performance.level}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Performance Insights</h4>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Most Active</span>
                <span className="font-bold text-blue-600">
                  {data.reduce((max, item) => item.workshop_count > max.workshop_count ? item : max, data[0])?.department}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...data.map(d => d.workshop_count))} workshops
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Best Performance</span>
                <span className="font-bold text-green-600">
                  {data.reduce((best, item) => 
                    (item.completion_rate + item.avg_progress) > (best.completion_rate + best.avg_progress) ? item : best, 
                    data[0]
                  )?.department}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.max(...data.map(d => d.completion_rate)).toFixed(1)}% completion
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-purple-600">Highest Value</span>
                <span className="font-bold text-purple-600">
                  {data.reduce((max, item) => item.total_savings > max.total_savings ? item : max, data[0])?.department}
                </span>
              </div>
              <div className="text-xs text-purple-500 mt-1">
                {formatCurrency(Math.max(...data.map(d => d.total_savings)))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DepartmentPerformanceComparisonChart;
